import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User } from '@supabase/supabase-js';

interface TicketFormProps {
  user: User;
  onTicketCreated: () => void;
}

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'feature-request', label: 'Feature Request' },
];

export function TicketForm({ user, onTicketCreated }: TicketFormProps) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('support_tickets' as any).insert({
        user_id: user.id,
        user_email: user.email,
        subject: subject.trim(),
        category,
        description: description.trim(),
      });

      if (error) throw error;

      // Send email notification (fire and forget)
      supabase.functions.invoke('send-ticket-notification', {
        body: {
          subject: subject.trim(),
          category,
          description: description.trim(),
          userEmail: user.email,
        },
      });

      setSubmitted(true);
      setSubject('');
      setCategory('general');
      setDescription('');
      onTicketCreated();

      toast({ title: 'Ticket submitted', description: 'We\'ll get back to you soon.' });

      setTimeout(() => setSubmitted(false), 3000);
    } catch (error: any) {
      toast({ title: 'Error', description: 'Failed to submit ticket.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <h4 className="font-medium text-foreground mb-4">Submit a Ticket</h4>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ticket-subject">Subject</Label>
          <Input
            id="ticket-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of your issue"
            required
            maxLength={200}
          />
        </div>
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ticket-desc">Description</Label>
          <Textarea
            id="ticket-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your issue in detail..."
            required
            maxLength={2000}
            className="min-h-[120px]"
          />
        </div>
        <Button type="submit" className="w-full gap-2" disabled={isSubmitting}>
          {isSubmitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
          ) : submitted ? (
            <><CheckCircle className="w-4 h-4" /> Submitted!</>
          ) : (
            <><Send className="w-4 h-4" /> Submit Ticket</>
          )}
        </Button>
      </form>
    </Card>
  );
}
